import { apiClient, type ApiResponse } from './client';
import type { User } from '../types';

interface LoginResponse {
  user: User;
  token: string;
}

interface XamanLoginData {
  uuid: string;
  qr_code: string;
  deep_link: string;
}

export async function initiateXamanLogin(): Promise<{ uuid: string; qrCode: string; deepLink: string }> {
  const response = await apiClient.post<ApiResponse<XamanLoginData>>('/auth/xaman/connect', {});
  
  if (!response.success || !response.data) {
    throw new Error(response.message || 'Failed to initiate Xaman login');
  }

  return {
    uuid: response.data.uuid,
    qrCode: response.data.qr_code,
    deepLink: response.data.deep_link,
  };
}

export async function completeXamanLogin(uuid: string, referralCode?: string): Promise<LoginResponse> {
  const body: { uuid: string; referral_code?: string } = { uuid };
  if (referralCode) {
    body.referral_code = referralCode;
  }

  const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/xaman/verify', body);

  if (!response.success || !response.data) {
    throw new Error(response.message || 'Authentication not completed');
  }

  return response.data;
}

export async function logout(): Promise<void> {
  try {
    await apiClient.post('/auth/logout');
  } catch (err) {
    console.error('Logout API error:', err);
  }
}

export async function getProfile(): Promise<User> {
  const response = await apiClient.get<ApiResponse<User>>('/auth/profile');

  if (!response.success || !response.data) {
    throw new Error('Failed to get user profile');
  }

  return response.data;
}

// ============================================================================
// Multi-Wallet API Functions
// ============================================================================

export interface LinkedWallet {
  id: string;
  user_id: string;
  xrpl_address: string;
  nickname: string | null;
  is_primary: boolean;
  created_at: string;
  last_used_at: string;
}

/**
 * Get all wallets linked to the current user
 */
export async function getLinkedWallets(): Promise<LinkedWallet[]> {
  const response = await apiClient.get<ApiResponse<LinkedWallet[]>>('/auth/wallets');

  if (!response.success || !response.data) {
    throw new Error('Failed to get linked wallets');
  }

  return response.data;
}

interface LinkWalletResponse {
  wallet: LinkedWallet;
  token: string;
}

/**
 * Link a new wallet to the current user's account
 * @param uuid - The Xaman verification UUID
 */
export async function linkWallet(uuid: string): Promise<LinkWalletResponse> {
  const response = await apiClient.post<ApiResponse<LinkWalletResponse>>('/auth/link-wallet', { uuid });

  if (!response.success || !response.data) {
    throw new Error((response as any).message || 'Failed to link wallet');
  }

  return response.data;
}

interface SwitchWalletResponse {
  token: string;
  xrpl_address: string;
}

/**
 * Switch to a different linked wallet
 * @param xrplAddress - The XRPL address to switch to
 */
export async function switchWallet(xrplAddress: string): Promise<SwitchWalletResponse> {
  const response = await apiClient.post<ApiResponse<SwitchWalletResponse>>('/auth/switch-wallet', {
    xrpl_address: xrplAddress,
  });

  if (!response.success || !response.data) {
    throw new Error((response as any).message || 'Failed to switch wallet');
  }

  return response.data;
}

/**
 * Unlink a wallet from the current user's account
 * @param xrplAddress - The XRPL address to unlink
 */
export async function unlinkWallet(xrplAddress: string): Promise<void> {
  const response = await apiClient.delete<ApiResponse<{ message: string }>>(`/auth/wallets/${xrplAddress}`);

  if (!response.success) {
    throw new Error((response as any).message || 'Failed to unlink wallet');
  }
}
